import { useCallback, useMemo, useState } from 'react';

type KnowledgeGraphError = { message: string } | null;

/**
 * Lightweight client hook that talks to our server-side Smithery proxy routes.
 *
 * This keeps the UI working even when the Smithery backend changes, and avoids
 * bundling any Smithery SDK code into the client.
 */
export function useKnowledgeGraph(userId: string) {
  const [error, setError] = useState<KnowledgeGraphError>(null);

  const getMemoryConnections = useCallback(
    async (memoryId: string) => {
      setError(null);

      const url = new URL('/api/smithery/connections', window.location.origin);
      url.searchParams.set('memoryId', memoryId);
      url.searchParams.set('userId', userId);

      const res = await fetch(url.toString());
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        const message = text || `Failed to fetch memory connections (${res.status})`;
        setError({ message });
        throw new Error(message);
      }

      return res.json();
    },
    [userId],
  );

  return useMemo(
    () => ({
      isReady: Boolean(userId),
      error,
      getMemoryConnections,
    }),
    [userId, error, getMemoryConnections],
  );
}